
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const valuePropositions = [
  {
    title: 'Strategic Advisory',
    description:
      'Comprehensive strategic guidance across the full investment lifecycle, from initial assessment through exit planning, ensuring optimal value creation at every stage.',
  },
  {
    title: 'Hands-On Support',
    description:
      'Active operational involvement with portfolio companies, providing expertise in scaling operations, optimizing processes, and navigating complex market dynamics.',
  },
  {
    title: 'Market Guidance',
    description:
      'Deep industry insights and market intelligence to help partners identify opportunities, mitigate risks, and position for sustainable competitive advantage.',
  },
  {
    title: 'Long-Term Partnership',
    description:
      'Committed to building enduring relationships that extend beyond traditional investment horizons, supporting partners through multiple growth phases and market cycles.',
  },
];

export default function ValueSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  return (
    <section
      id="value"
      ref={sectionRef}
      className="relative min-h-screen py-32 bg-gray-50 overflow-hidden"
    >
      {/* Regular Grid Background */}
      <motion.div style={{ y: backgroundY }} className="absolute inset-0">
        <div className="absolute inset-0 bg-gray-50"></div>
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `
              linear-gradient(to right, #9ca3af 1px, transparent 1px),
              linear-gradient(to bottom, #9ca3af 1px, transparent 1px)
            `,
            backgroundSize: '77px 77px',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 via-transparent to-gray-50/50"></div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.h2 
            className="text-5xl font-light text-blue-900 mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Value
          </motion.h2>
          <motion.div 
            className="w-24 h-1 bg-blue-900 mx-auto"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          />
        </div>

        {/* Value Tiles Grid */}
        <div className="grid md:grid-cols-2 gap-16 max-w-6xl mx-auto">
          {valuePropositions.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              className="group flex flex-col items-center text-center"
            >
              <div className="w-[calc(100vw-3rem)] max-w-[420px] h-[calc(100vw-3rem)] max-h-[420px] aspect-square rounded-full bg-white border-2 border-blue-200/30 shadow-lg hover:shadow-xl hover:border-blue-300/50 transition-all duration-300 relative overflow-hidden flex flex-col items-center justify-center p-8 md:p-14">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>

                <div className="relative z-10">
                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-semibold text-blue-900 mb-4 md:mb-8">{value.title}</h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed text-base md:text-xl">{value.description}</p>
                </div>

                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-100/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
